/**
 * Free Gift Auto-Add (Spend $100, get free Grifter Pale Ale 4-Pack)
 *
 * Watches the cart subtotal and automatically adds/removes a free gift
 * variant once the customer's cart (excluding the gift itself) reaches
 * the configured threshold. Pairs with the automatic Shopify discount
 * "Free Pale Ale Gift - Spend $100", which zeroes the gift's price at
 * checkout — this script only handles getting it in and out of the cart.
 *
 * Hooks into Baseline's `theme:cart:change` event (fired on add-to-cart
 * and line-item changes) so it stays in sync with drawer, cart page,
 * and any other ajax cart update, without polling.
 */

(() => {
  const GIFT_VARIANT_ID = 45382083313846; // Grifter Pale Ale (375ml) - 4 Pack
  const THRESHOLD_CENTS = 10000; // $100.00 AUD
  const PROPERTY_KEY = '_free_gift_bxgy'; // marks the line item as auto-added

  let syncing = false; // prevents overlapping add/remove calls
  let pendingResync = false;

  async function getCart() {
    return freshJSON('/cart.js');
  }

  function findGiftLine(cart) {
    return cart.items.find(
      (item) =>
        item.variant_id === GIFT_VARIANT_ID &&
        item.properties &&
        item.properties[PROPERTY_KEY] === 'true'
    );
  }

  function nonGiftSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      const isGiftLine =
        item.variant_id === GIFT_VARIANT_ID &&
        item.properties &&
        item.properties[PROPERTY_KEY] === 'true';

      return isGiftLine ? total : total + item.final_line_price;
    }, 0);
  }

  async function addGift() {
    const response = await fetch('/cart/add.js', {
      ...fetchConfigDefaults(),
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: {
              [PROPERTY_KEY]: 'true',
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // Likely out of stock — fail quietly, don't block the customer's cart.
      console.warn('Free gift could not be added:', data.description || data);
      return false;
    }

    return true;
  }

  async function removeGift(giftLine) {
    const response = await fetch('/cart/change.js', {
      ...fetchConfigDefaults(),
      body: JSON.stringify({
        id: giftLine.key,
        quantity: 0,
      }),
    });

    return response.ok;
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  }

  async function sync() {
    if (syncing) {
      pendingResync = true;
      return;
    }

    syncing = true;

    try {
      const cart = await getCart();
      const giftLine = findGiftLine(cart);
      const qualifies = nonGiftSubtotal(cart) >= THRESHOLD_CENTS;

      let changed = false;

      if (qualifies && !giftLine) {
        changed = await addGift();
      } else if (!qualifies && giftLine) {
        changed = await removeGift(giftLine);
      }

      if (changed) {
        refreshCartUI();
      }
    } catch (e) {
      console.error('Free gift sync error:', e);
    } finally {
      syncing = false;

      if (pendingResync) {
        pendingResync = false;
        sync();
      }
    }
  }

  document.addEventListener('theme:cart:change', sync);

  // Catch carts that already qualify on page load (e.g. customer returns
  // to a previous session, or cart was modified outside this script).
  window.switchOnDOMContentLoaded(() => {
    sync();
  });
})();
